import React, { FC, useMemo } from 'react';
import { VideoBackgroundRenderer } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/components/HeaderV2/layouts';
import type { VideoArchProps } from './VideoArch.types';
import { Box } from '@withjoy/joykit';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { VideoArchPhoto } from '../VideoArchPhoto/VideoArchPhoto';
import { pxToRem } from '@withjoy/joykit/theme';

// Arch aspect ratio: 6/5 = 1.2
// Target: arch height ≈ 80% of viewport height
// Calculation: maxWidth = 0.8 * vh * aspectRatio
// For 16:9 screens (2K monitors): maxWidth = 0.8 * vw * 9/16 * 1.2 ≈ 0.54vw
// For 16:10 screens (MacBooks): maxWidth = 0.8 * vw * 10/16 * 1.2 ≈ 0.6vw
//
// Responsive max widths based on common desktop screens:
// - Small desktop (1280px): ~900px (keeps height ≈ 80% of vh)
// - Standard desktop (1440px+): ~1100px (14" MacBook, 2K monitors)
// - Large desktop (1920px+): ~1300px (16" MacBook, ultra-wide)
//
// Breakpoints: md3=1280px, lg=1440px, xl=1920px
const ARCH_MAX_WIDTHS_RESPONSIVE = {
  _: '100%', // Mobile fallback
  md3: pxToRem(900), // Small desktop (1280px): keeps arch ≈80% vh
  lg: pxToRem(1100), // // Standard desktop (1440px): optimal for 14" MacBook, 2K monitors
  xl: pxToRem(1300), // Large desktop (1920px+): optimal for 16" MacBook, ultra-wide
};

export const VideoArch: FC<VideoArchProps> = ({ photoProps, videoProps, renderWrapper }) => {
  const context = { photoProps, videoProps };
  const isMobileScreen = useIsMobileScreen();

  const { archWidth, archLeft, archHeight, archTransform } = useMemo(() => {
    if (isMobileScreen) {
      // On mobile arch height is set to 90% of container height
      // The width might overflow but will be truncated symmetrically on left and right
      return {
        archWidth: '100%',
        archLeft: 0,
        archHeight: '90%',
        archTransform: 'none',
      };
    }
    return {
      // On desktop, arch is centered and constrained to 80% of viewport width
      archWidth: '80vw',
      // Use transform to center based on actual width (works with maxWidth constraint)
      archLeft: '50%',
      archTransform: 'translateX(-50%)',
      archHeight: '100%',
    };
  }, [isMobileScreen]);

  const photoArchContent = <VideoArchPhoto {...photoProps} />;

  // Add support for custom wrappers like inline editor
  const wrappedContent = (
    <Box
      width={archWidth}
      left={archLeft}
      transform={archTransform}
      height={archHeight}
      maxWidth={ARCH_MAX_WIDTHS_RESPONSIVE}
      display="flex"
      justifyContent="center"
      position="absolute"
      bottom={0}
      zIndex="docked"
    >
      {renderWrapper ? (
        renderWrapper(photoArchContent, context)
      ) : (
        <Box
          position="absolute"
          bottom={0}
          left={0}
          zIndex="docked"
        >
          {photoArchContent}
        </Box>
      )}
    </Box>
  );

  const hasContent = videoProps.videoUrl || videoProps.thumbnailUrl;
  const containerHeight = videoProps.height ?? '100vh';

  return (
    <Box
      position={isMobileScreen ? 'absolute' : 'relative'}
      height={containerHeight}
      width="100vw"
      left="calc(-50vw + 50%)"
      overflow="hidden"
      // Add gap between the video and navbar
      marginTop={{ _: 0, sm4: 9 }}
    >
      {/* Placeholder is handled below as a full bleed div below. Hence we don't need to show it here.*/}
      {hasContent ? (
        <VideoBackgroundRenderer
          {...videoProps}
          showPlaceholder={false}
        >
          {wrappedContent}
        </VideoBackgroundRenderer>
      ) : (
        <Box>
          <Box
            position="absolute"
            top={0}
            width="100%"
            height={containerHeight}
            backgroundColor={videoProps.showPlaceholder ? 'lightgray' : photoProps.innerArchColor}
            zIndex="base"
            overflow="hidden"
          />
          {wrappedContent}
        </Box>
      )}
    </Box>
  );
};
